import { Loading } from "@/components/common/Loading/Loading"
import { toast } from "@/components/common/Toast"
import { useQuestionnaireStatusNavigation } from "@/hooks/useQuestionnaireStatusNavigation/useQuestionnaireStatusNavigation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { usePollAutofillStatus } from "@/services/api/questionAnsweringTaskGroupService"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import { useGetQuestionnaireData } from "@/services/api/questionnaireService"
import { QuestionAnsweringTaskGroup } from "@/types/questionAnswering"
import { classNames } from "@/utils/classNames"
import { useEffect, useMemo, useRef, useState } from "react"

export interface AnswerGenerationLoaderProps {
  autofillTaskGroupId: string
  questionnaireId: string
}

const calcAutofillProgress = (
  taskGroup: QuestionAnsweringTaskGroup,
): number => {
  const processedSubtasks = taskGroup.subtasks.filter(
    (subtask) => subtask.processed,
  )

  return Math.round(
    (processedSubtasks.length / taskGroup.subtasks.length) * 100,
  )
}

export const AnswerGenerationLoader: React.FC<AnswerGenerationLoaderProps> = ({
  autofillTaskGroupId,
  questionnaireId,
}) => {
  const { translate } = useTranslation()
  const [autofillProgress, setAutofillProgress] = useState(0)
  const startTimeRef = useRef<number>(Date.now())

  const {
    data: questionnaire,
    refetch: refetchQuestionnaire,
    error: questionnaireError,
  } = useGetQuestionnaireData(questionnaireId)

  const { data: autofillTaskGroup, isError: isAutofillTaskGroupError } =
    usePollAutofillStatus(
      questionnaireId,
      autofillTaskGroupId,
      3000,
      !!questionnaire && !isQuestionnaireNotFound(questionnaireError),
    )

  const subtaskProcessedStatuses = useMemo(
    () => autofillTaskGroup?.subtasks.map((subtask) => subtask.processed) ?? [],
    [autofillTaskGroup],
  )

  const [, setQuestionnaireStatus] =
    useQuestionnaireStatusNavigation(questionnaire)

  useEffect(() => {
    if (autofillTaskGroup) {
      const progress = calcAutofillProgress(autofillTaskGroup)
      setAutofillProgress(progress)
    }
  }, [subtaskProcessedStatuses, autofillTaskGroup])

  useEffect(() => {
    const handleAutofillUpdate = async () => {
      if (isAutofillTaskGroupError) {
        toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
        trackEvent(AnalyticsEvent.AUTO_FILL_FAILED, {
          questionnaire_id: questionnaireId,
          error_message: "Autofill task group error",
        })
      } else if (autofillTaskGroup && autofillTaskGroup.processed) {
        toast.success(
          translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_FINISHED"),
          undefined,
          {
            id: "toast-success-autofill",
          },
        )

        const durationSeconds = Math.round(
          (Date.now() - startTimeRef.current) / 1000,
        )
        trackEvent(AnalyticsEvent.AUTO_FILL_COMPLETED, {
          questionnaire_id: questionnaireId,
          duration_seconds: durationSeconds,
          answer_count: autofillTaskGroup.subtasks.length,
        })

        // force refetch and cache invalidation of questionnaire by refetch
        const refetched = await refetchQuestionnaire()
        setQuestionnaireStatus(refetched.data?.status ?? questionnaire?.status)
      }
    }
    handleAutofillUpdate()
  }, [
    isAutofillTaskGroupError,
    autofillTaskGroup,
    subtaskProcessedStatuses,
    refetchQuestionnaire,
    setQuestionnaireStatus,
    questionnaire?.status,
    translate,
    questionnaireId,
  ])

  let rollingText: string
  if (autofillProgress > 60) {
    rollingText = translate(
      "QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS_LOADING_3",
    )
  } else if (autofillProgress > 20) {
    rollingText = translate(
      "QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS_LOADING_2",
    )
  } else {
    rollingText = translate("QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS_LOADING")
  }

  return (
    <div
      className={classNames(
        "flex h-full flex-col items-center justify-center",
        "px-XXL",
        "text-center",
      )}
    >
      <Loading progressValue={autofillProgress} text={rollingText} />
    </div>
  )
}
